import { Skeleton, Stack, Box } from '@mui/material';
import { AutomationDialogContent } from './AutomationDialogContent';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import {
  ConfigurationCanvasLeftPanel,
  ConfigurationCanvasPanelContent,
  ConfigurationCanvasPanelHeader,
} from '../pages/configuration-canvas-panel';
import useAutomationsCanvasInteractions from '../pages/canvas-pages/useAutomationsCanvasInteractions';
import { automationsSortOptions } from '../pages/canvas-pages/consts';
import { WithEnvConnectionErrorPanel } from '../pages/canvas-pages/united-canvas/canvas-page/error-panel/EnvConnectionErrorPanel';
import { getPaywallSourceFromAutomationVariant } from '../dashboard/paywall/sales-activated-trial/helper';
import PaywallVerticalLayout from '../dashboard/paywall/sales-activated-trial/PaywallVerticalLayout';
import usePaywall, { PaywallAction } from '../dashboard/paywall/usePaywall';
import { useGetAutomationFilters } from '../pages/canvas-pages/automationFilters';
import { HORIZONTAL_PADDING } from '../pages/configuration-canvas-panel/consts';

interface AutomationDialogProps {
  onSaveOrCreate: (
    automationJson: AutomationStructureNew,
    crmOrgIds: string[],
    isEdit: boolean,
  ) => any;
  onToggleActivation: (
    automationJson: AutomationStructureNew,
    deployToOrgIds: string[],
    isActive: boolean,
  ) => any;
  onDeleteAutomation: (automationId: string, automationType: AutomationType) => any;
  automations: AutomationStructureNew[];
  crmOrgId: string;
  funnelMap: FunnelMap;
  isLoading?: boolean;
  automationVariant: AutomationFormVariant;
  onDeployAutomation: (
    deployAutomationDto: AutomationStructureNew,
    deployToOrgIds: string[],
  ) => any;
  onClose?: () => void;
  disableCanvasTemplates?: boolean;
  excludeAutomationsNotInFunnelMapObjects?: boolean;
}

export const AutomationDialog = ({
  crmOrgId,
  automations,
  onSaveOrCreate,
  funnelMap,
  isLoading,
  onToggleActivation,
  onDeleteAutomation,
  automationVariant,
  onDeployAutomation,
  onClose,
  disableCanvasTemplates,
  excludeAutomationsNotInFunnelMapObjects,
}: AutomationDialogProps) => {
  const { setHoveredItem } = useAutomationsCanvasInteractions();

  const { paywallAction } = usePaywall();
  const isPaywallV2Block = paywallAction === PaywallAction.TALK_TO_SALES;
  const paywallSource = getPaywallSourceFromAutomationVariant(automationVariant);
  const isExpandAllowed = !isPaywallV2Block;

  const filters = useGetAutomationFilters(automations);

  const headerContent = (
    <ConfigurationCanvasPanelHeader onClose={onClose} withExpandedModeButton={isExpandAllowed}>
      {automationVariant === AutomationFormVariant.PLAYBOOK_ALERT ? 'Alerts' : 'Automations'}
    </ConfigurationCanvasPanelHeader>
  );

  if (isLoading) {
    return (
      <ConfigurationCanvasLeftPanel>
        <WithEnvConnectionErrorPanel shouldWaitForInitialFetch={false}>
          {headerContent}
          <ConfigurationCanvasPanelContent>
            <Stack spacing={2} direction="column">
              <Skeleton variant="text" width={'80%'} height={46} />
              <Skeleton variant="rectangular" width={296} height={42} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
            </Stack>
          </ConfigurationCanvasPanelContent>
        </WithEnvConnectionErrorPanel>
      </ConfigurationCanvasLeftPanel>
    );
  }

  return (
    <ConfigurationCanvasLeftPanel
      sx={{
        '.configuration-canvas-panel-content': {
          overflow: 'hidden',
        },
      }}
      withExpandedModeButton={isExpandAllowed}
    >
      <WithEnvConnectionErrorPanel shouldWaitForInitialFetch={false}>
        {headerContent}
        <ConfigurationCanvasPanelContent
          sx={{
            padding: 0, //needed in order to override the existing value
          }}
        >
          {isPaywallV2Block && (
            <Box
              sx={{
                padding: `0 ${HORIZONTAL_PADDING}px 16px`,
                height: '100%',
              }}
            >
              <PaywallVerticalLayout paywallSource={paywallSource} variant="regular" />
            </Box>
          )}
          {!isPaywallV2Block && (
            <AutomationDialogContent
              onSaveOrCreate={onSaveOrCreate}
              onToggleActivation={onToggleActivation}
              onDeleteAutomation={onDeleteAutomation}
              automations={automations}
              crmOrgId={crmOrgId}
              recordTypesData={funnelMap.recordTypesData}
              funnelsData={funnelMap.funnelsData}
              sortOptions={automationsSortOptions}
              setHoveredItem={setHoveredItem}
              onDeployAutomation={onDeployAutomation}
              disableCanvasTemplates={disableCanvasTemplates}
              excludeAutomationsNotInFunnelMapObjects={excludeAutomationsNotInFunnelMapObjects}
              filtersForExpandedMode={filters}
            />
          )}
        </ConfigurationCanvasPanelContent>
      </WithEnvConnectionErrorPanel>
    </ConfigurationCanvasLeftPanel>
  );
};
